import { RowCrudStatus } from "../../common/types";
import {
  CreditPurchase,
  CreditPurchaseCreateMutationVariables,
} from "../../../api/graphql";
import { LangStore } from "../../../store";
import { graphqlToCalendarDate } from "../../../common/utils";
import { tenantDisplayId } from "../../tenant/store/TenantCommonStore";
import { diaDeCalendario } from "../../../common/calendarDate";
import { blankToNull } from "../../../common/blankToNull";

export type CreditPurchaseValuesType = {
  id: string;
  displayId: string;
  version_: string;
  __status__: RowCrudStatus;
  __tenant: string;
  showTenant: { displayId: string; id: string } | null;
  quantity: number;
  purchaseDate: string | null;
  expirationDate: string | null;
  unitPrice: number | undefined;
  notes: string;
};

export type CreditPurchaseErrorsType = {
  tenant?: { message: string; fieldLabel: string };
  quantity?: { message: string; fieldLabel: string };
  purchaseDate?: { message: string; fieldLabel: string };
  expirationDate?: { message: string; fieldLabel: string };
  unitPrice?: { message: string; fieldLabel: string };
  notes?: { message: string; fieldLabel: string };
};

export function CreditPurchaseValuesInit(): CreditPurchaseValuesType {
  return {
    id: "",
    displayId: "",
    version_: "",
    __status__: "empty",
    __tenant: "",
    showTenant: null,
    quantity: 0,
    purchaseDate: null,
    expirationDate: null,
    unitPrice: undefined,
    notes: "",
  };
}

export type CreditPurchaseType = CreditPurchase | CreditPurchaseValuesType;

export function creditPurchaseDisplayId(
  source: Partial<CreditPurchase>,
  _langStore: LangStore
) {
  return (
    (source.purchaseDate === null || source.purchaseDate === undefined
      ? _langStore.emptyStringSymbol
      : graphqlToCalendarDate(source.purchaseDate)) +
    " - " +
    (source.quantity?.toString() ?? _langStore.emptyStringSymbol)
  );
}

export function getCreditPurchaseValues(
  source: CreditPurchaseType,
  _langStore: LangStore,
  _duplicated: boolean = false
): CreditPurchaseValuesType {
  const entityValues = CreditPurchaseValuesInit();
  entityValues.id = source.id;
  entityValues.displayId =
    "__status__" in source
      ? source.displayId
      : creditPurchaseDisplayId(source, _langStore);
  entityValues.version_ = source.version_;
  entityValues.showTenant =
    "tenant" in source && source.tenant
      ? {
          id: source.tenant.id,
          displayId: tenantDisplayId(source.tenant, _langStore),
        }
      : "showTenant" in source && source.showTenant
      ? { id: source.showTenant.id, displayId: source.showTenant.displayId }
      : null;
  entityValues.__tenant = entityValues.showTenant?.displayId || "";
  entityValues.quantity = source.quantity || 0;
  entityValues.purchaseDate = diaDeCalendario(source.purchaseDate);
  entityValues.expirationDate = diaDeCalendario(source.expirationDate);
  entityValues.unitPrice =
    (source.unitPrice as unknown) === "" ||
    source.unitPrice === undefined ||
    source.unitPrice === null
      ? undefined
      : source.unitPrice;
  entityValues.notes = source.notes || "";
  if (_duplicated) {
    entityValues.__status__ = "added";
  } else if ("__status__" in source) {
    entityValues.__status__ = source.__status__;
  }
  return entityValues;
}

export function getCreditPurchaseCreateVariables(
  values: CreditPurchaseValuesType
): CreditPurchaseCreateMutationVariables {
  return {
    tenantId: values.showTenant?.id || "",
    quantity: values.quantity,
    purchaseDate: values.purchaseDate!,
    expirationDate: values.expirationDate || null,
    unitPrice: values.unitPrice === undefined ? null : values.unitPrice,
    notes: blankToNull(values.notes),
  };
}
